import React, { useState, useEffect, useCallback } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import { Search, MapPin, Mail, Phone, ExternalLink, Users, Network, ChevronLeft, ChevronRight } from 'lucide-react';
import { Planet } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MemberDirectoryPage = () => {
  const { language } = useLanguage();
  const { token } = useOrbitAuth();
  const t = (fr, en) => language === 'fr' ? fr : en;

  const [activeTab, setActiveTab] = useState('community');
  const [search, setSearch] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = activeTab === 'community' ? 'members/community' : 'members/network';
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      const res = await fetch(`${API}/api/orbit/${endpoint}?${params}`, { headers });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMembers(data.members || data.affiliates || []);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch { toast.error(t('Erreur de chargement', 'Loading error')); }
    finally { setLoading(false); }
  }, [activeTab, search, page, token]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const rankConfig = {
    mercure: { label: t('Mercure', 'Mercury'), color: 'var(--planet-mercury)', bg: 'bg-gray-100 text-gray-600' },
    venus: { label: t('Venus', 'Venus'), color: 'var(--planet-venus)', bg: 'bg-yellow-50 text-yellow-700' },
    terre: { label: t('Terre', 'Earth'), color: 'var(--planet-earth)', bg: 'bg-blue-50 text-blue-700' },
    mars: { label: 'Mars', color: 'var(--planet-mars)', bg: 'bg-red-50 text-red-700' },
    jupiter: { label: 'Jupiter', color: 'var(--planet-jupiter)', bg: 'bg-orange-50 text-orange-700' },
    saturne: { label: t('Saturne', 'Saturn'), color: 'var(--planet-saturn)', bg: 'bg-amber-50 text-amber-700' },
  };

  const MemberCard = ({ member, isAffiliate }) => {
    const rank = rankConfig[member.rank] || rankConfig.mercure;
    const socials = member.socials || {};
    const hasContact = member.email || member.phone || member.messenger_link || socials.messenger_link;
    const messengerLink = member.messenger_link || socials.messenger_link;

    return (
      <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`member-card-${member.user_id}`}>
        {/* Header — Avatar + Name + Rank */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
            style={{ backgroundColor: rank.color }}>
            {member.picture ? (
              <img src={member.picture} alt={member.name} className="w-12 h-12 rounded-full object-cover" />
            ) : (
              member.name?.charAt(0)?.toUpperCase() || 'M'
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-[var(--ls-text)] truncate">{member.name}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              {member.rank && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${rank.bg}`}>
                  <Planet className="w-3 h-3" weight="fill" />{rank.label}
                </span>
              )}
              {isAffiliate && member.is_direct && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--ls-terracotta)]/10 text-[var(--ls-terracotta)]">
                  {t('Direct', 'Direct')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Location */}
        {member.city && (
          <div className="flex items-center gap-1.5 text-sm text-[var(--ls-text-muted)] mb-3">
            <MapPin className="w-3.5 h-3.5 text-[var(--ls-terracotta)]" />
            <span>{member.city}{member.province_code ? `, ${member.province_code}` : ''}</span>
          </div>
        )}

        {/* Bio */}
        {(member.bio_fr || member.bio_en) && (
          <p className="text-sm text-[var(--ls-text-muted)] mb-3 line-clamp-2">
            {language === 'fr' ? (member.bio_fr || member.bio_en) : (member.bio_en || member.bio_fr)}
          </p>
        )}

        {/* Contact Buttons */}
        {hasContact && (
          <div className="flex flex-wrap gap-2 pt-3 border-t border-[hsl(40,20%,88%)]">
            {member.email && (
              <a href={`mailto:${member.email}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--ls-sand-dark)] rounded-full text-xs font-medium text-[var(--ls-text)] hover:bg-[var(--ls-terracotta)]/10 hover:text-[var(--ls-terracotta)] transition-colors"
                data-testid="contact-email">
                <Mail className="w-3 h-3" />{t('Courriel', 'Email')}
              </a>
            )}
            {member.phone && (
              <a href={`tel:${member.phone}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--ls-sand-dark)] rounded-full text-xs font-medium text-[var(--ls-text)] hover:bg-[var(--ls-forest)]/10 hover:text-[var(--ls-forest)] transition-colors"
                data-testid="contact-phone">
                <Phone className="w-3 h-3" />{t('Appeler', 'Call')}
              </a>
            )}
            {messengerLink && (
              <a href={messengerLink} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--ls-sand-dark)] rounded-full text-xs font-medium text-[var(--ls-text)] hover:bg-blue-50 hover:text-blue-600 transition-colors"
                data-testid="contact-messenger">
                <ExternalLink className="w-3 h-3" />Messenger
              </a>
            )}
            {socials.website && (
              <a href={socials.website} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--ls-sand-dark)] rounded-full text-xs font-medium text-[var(--ls-text)] hover:bg-[var(--ls-gold)]/10 hover:text-[var(--ls-gold)] transition-colors"
                data-testid="contact-website">
                <ExternalLink className="w-3 h-3" />{t('Site web', 'Website')}
              </a>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <OrbitLayout>
      <div className="space-y-6" data-testid="member-directory-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--ls-text)]">
            {t('Annuaire des membres', 'Member Directory')}
          </h1>
          <p className="text-[var(--ls-text-muted)] mt-1">
            {t("Trouvez et connectez avec d'autres membres", 'Find and connect with other members')}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex gap-2" data-testid="directory-tabs">
            <button onClick={() => { setActiveTab('community'); setPage(1); setSearch(''); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                activeTab === 'community'
                  ? 'bg-[var(--ls-terracotta)] text-white shadow-md shadow-[var(--ls-terracotta)]/20'
                  : 'bg-white border border-[hsl(40,20%,88%)] text-[var(--ls-text-muted)] hover:bg-[var(--ls-sand-dark)]'
              }`} data-testid="tab-community">
              <Users className="w-4 h-4" />
              {t('Communaute', 'Community')}
            </button>
            <button onClick={() => { setActiveTab('network'); setPage(1); setSearch(''); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                activeTab === 'network'
                  ? 'bg-[var(--ls-forest)] text-white shadow-md shadow-[var(--ls-forest)]/20'
                  : 'bg-white border border-[hsl(40,20%,88%)] text-[var(--ls-text-muted)] hover:bg-[var(--ls-sand-dark)]'
              }`} data-testid="tab-network">
              <Network className="w-4 h-4" />
              {t('Mon Reseau', 'My Network')}
            </button>
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ls-text-muted)]" />
            <input type="text" value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder={t('Rechercher par nom, ville...', 'Search by name, city...')}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[hsl(40,20%,88%)] bg-white text-sm text-[var(--ls-text)] focus:outline-none focus:border-[var(--ls-terracotta)] transition-colors"
              data-testid="member-search" />
          </div>
        </div>

        {/* Info Banner */}
        <div className={`rounded-2xl p-4 text-sm ${
          activeTab === 'community'
            ? 'bg-[var(--ls-terracotta)]/5 border border-[var(--ls-terracotta)]/15 text-[var(--ls-terracotta)]'
            : 'bg-[var(--ls-forest)]/5 border border-[var(--ls-forest)]/15 text-[var(--ls-forest)]'
        }`}>
          {activeTab === 'community' ? (
            <p>
              <Users className="w-4 h-4 inline-block mr-1 -mt-0.5" />
              {t(
                `${total} membre${total !== 1 ? 's' : ''} visible${total !== 1 ? 's' : ''} dans la communaute. Rendez-vous visible depuis votre profil !`,
                `${total} member${total !== 1 ? 's' : ''} visible in the community. Make yourself visible from your profile!`
              )}
            </p>
          ) : (
            <p>
              <Network className="w-4 h-4 inline-block mr-1 -mt-0.5" />
              {t(
                `${total} affilie${total !== 1 ? 's' : ''} dans votre reseau MLM.`,
                `${total} affiliate${total !== 1 ? 's' : ''} in your MLM network.`
              )}
            </p>
          )}
        </div>

        {/* Members Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-3 border-[var(--ls-terracotta)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[hsl(40,20%,88%)] rounded-2xl">
            <Users className="w-14 h-14 text-[var(--ls-text-muted)]/20 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-[var(--ls-text)] mb-1">
              {activeTab === 'community'
                ? t('Aucun membre visible', 'No visible members')
                : t('Aucun affilie trouve', 'No affiliates found')}
            </h3>
            <p className="text-sm text-[var(--ls-text-muted)]">
              {activeTab === 'community'
                ? t('Soyez le premier a vous rendre visible !', 'Be the first to make yourself visible!')
                : t('Votre reseau est vide pour le moment.', 'Your network is empty for now.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="members-grid">
            {members.map(member => (
              <MemberCard key={member.user_id} member={member} isAffiliate={activeTab === 'network'} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3" data-testid="members-pagination">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-2 rounded-lg bg-white border border-[hsl(40,20%,88%)] disabled:opacity-30 hover:bg-[var(--ls-sand-dark)] transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-[var(--ls-text-muted)]">
              {t('Page', 'Page')} {page} / {totalPages} ({total} {t('total', 'total')})
            </span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-2 rounded-lg bg-white border border-[hsl(40,20%,88%)] disabled:opacity-30 hover:bg-[var(--ls-sand-dark)] transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </OrbitLayout>
  );
};

export default MemberDirectoryPage;
